import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Header from '../components/Header';
import StatCard from '../components/StatCard';
import ConnectionsPanel from '../components/ConnectionsPanel';
import ProfileImage from '../components/ProfileImage';
import { formatLapTime, formatDate, formatSpeed } from '../utils/format';
import { getSharedSessions, getRequests } from '../utils/sharingService';
import { authHeaders } from '../utils/authService';
import SessionTile, { getSessionDateLabel, formatSessionTypeLabel } from '../components/SessionTile';
import { SessionTypeIcon } from '../utils/RaceImageMapper';
import { TrackTypeIcon, normalizeTrackTypeValue } from '../utils/TrackTypeIcon';
import LiveTelemetryPanel from '../components/LiveTelemetryPanel';
import LoadingProgress from '../components/LoadingProgress';
import TrackThumbnail from '../components/TrackThumbnail';

const styles = {
  page: {
    minHeight: '100vh',
    background: 'var(--color-bg)',
  },
  content: {
    maxWidth: 1000,
    margin: '0 auto',
    padding: '32px 24px',
  },
  title: {
    fontFamily: 'var(--font-heading)',
    fontSize: 18,
    fontWeight: 700,
    color: 'var(--color-secondary)',
    letterSpacing: 2,
    marginBottom: 24,
  },
  sectionRow: {
    display: 'flex',
    gap: 12,
    marginBottom: 24,
  },
  filterRow: {
    display: 'flex',
    gap: 12,
    marginBottom: 24,
    flexWrap: 'wrap',
  },
  filterGroup: {
    minWidth: 220,
    flex: '1 1 220px',
  },
  filterLabel: {
    fontFamily: 'var(--font-heading)',
    fontSize: 10,
    color: 'var(--color-text-dim)',
    letterSpacing: 1.5,
    textTransform: 'uppercase',
    marginBottom: 8,
  },
  filterSelect: {
    width: '100%',
    padding: '10px 14px',
    background: 'var(--color-surface)',
    border: '1px solid var(--color-border)',
    borderRadius: 8,
    color: 'var(--color-text)',
    fontFamily: 'var(--font-body)',
    fontSize: 14,
    outline: 'none',
  },
  resetBtn: {
    padding: '10px 18px',
    background: 'transparent',
    border: '1px solid var(--color-border)',
    borderRadius: 8,
    color: 'var(--color-text)',
    fontFamily: 'var(--font-heading)',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 1.2,
    cursor: 'pointer',
    textTransform: 'uppercase',
    alignSelf: 'flex-end',
  },
  tab: {
    padding: '8px 20px',
    background: 'var(--color-surface)',
    border: '1px solid var(--color-border)',
    borderRadius: 6,
    color: 'var(--color-text-dim)',
    fontFamily: 'var(--font-heading)',
    fontSize: 11,
    letterSpacing: 1.5,
    cursor: 'pointer',
    textTransform: 'uppercase',
  },
  tabActive: {
    background: 'var(--color-primary)',
    borderColor: 'var(--color-primary)',
    color: '#fff',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
    gap: 16,
  },
  sessionSection: {
    marginBottom: 28,
  },
  sectionDivider: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 14,
  },
  sectionDividerLine: {
    flex: 1,
    height: 1,
    background: 'var(--color-border)',
  },
  sectionDividerLabel: {
    fontFamily: 'var(--font-heading)',
    fontSize: 11,
    color: 'var(--color-text-dim)',
    letterSpacing: 1.6,
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
  },
  groupedTrackSection: {
    marginBottom: 28,
  },
  groupedTrackHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 14,
    cursor: 'pointer',
    userSelect: 'none',
    padding: '6px 0',
    borderRadius: 6,
    transition: 'background 0.15s',
  },
  groupedTrackChevron: {
    fontFamily: 'var(--font-body)',
    fontSize: 14,
    color: 'var(--color-text-dim)',
    transition: 'transform 0.2s',
    lineHeight: 1,
    flexShrink: 0,
  },
  groupedTrackLabel: {
    fontFamily: 'var(--font-heading)',
    fontSize: 12,
    fontWeight: 700,
    color: 'var(--color-text)',
    letterSpacing: 0.8,
    whiteSpace: 'nowrap',
  },
  groupedTrackMeta: {
    fontFamily: 'var(--font-body)',
    fontSize: 12,
    color: 'var(--color-text-dim)',
    whiteSpace: 'nowrap',
  },
  groupedTrackLine: {
    flex: 1,
    height: 1,
    background: 'var(--color-border)',
  },
  groupedTrackDeleteBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 4,
    borderRadius: 4,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0.4,
    transition: 'opacity 0.15s',
    flexShrink: 0,
  },
  card: {
    background: 'var(--color-surface)',
    border: '1px solid var(--color-border)',
    borderRadius: 10,
    padding: '20px',
    cursor: 'pointer',
    transition: 'border-color 0.2s',
  },
  trackName: {
    fontFamily: 'var(--font-heading)',
    fontSize: 14,
    fontWeight: 700,
    color: 'var(--color-text)',
    letterSpacing: 0.5,
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
  },
  emptyTitle: {
    fontFamily: 'var(--font-heading)',
    fontSize: 16,
    color: 'var(--color-text)',
    letterSpacing: 1,
    marginBottom: 12,
  },
  emptyText: {
    fontFamily: 'var(--font-body)',
    fontSize: 14,
    color: 'var(--color-text-dim)',
    lineHeight: 1.6,
  },
  trackListCard: {
    background: 'var(--color-surface)',
    border: '1px solid var(--color-border)',
    borderRadius: 10,
    padding: '16px 20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    transition: 'border-color 0.2s',
  },
  trackListName: {
    fontFamily: 'var(--font-heading)',
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--color-text)',
    letterSpacing: 0.5,
  },
  trackListDetail: {
    fontFamily: 'var(--font-body)',
    fontSize: 12,
    color: 'var(--color-text-dim)',
    marginTop: 4,
  },
  customBadge: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 10,
    fontSize: 10,
    fontWeight: 600,
    fontFamily: 'var(--font-body)',
    background: 'rgba(46, 134, 193, 0.15)',
    color: '#2e86c1',
    marginLeft: 8,
  },
  loading: {
    fontFamily: 'var(--font-body)',
    fontSize: 14,
    color: 'var(--color-text-dim)',
    textAlign: 'center',
    padding: 40,
  },
  statsRow: {
    display: 'flex',
    gap: 12,
    marginBottom: 24,
    flexWrap: 'wrap',
  },
  sessionTypeSummaryCard: {
    background: 'var(--color-surface)',
    border: '1px solid var(--color-border)',
    borderRadius: 8,
    padding: '16px 20px',
    minWidth: 220,
    flex: '1 1 220px',
  },
  sessionTypeSummaryLabel: {
    fontFamily: 'var(--font-heading)',
    fontSize: 10,
    fontWeight: 500,
    color: 'var(--color-text-dim)',
    textTransform: 'uppercase',
    letterSpacing: 1.5,
    marginBottom: 10,
  },
  sessionTypeSummaryList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  sessionTypeSummaryRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  sessionTypeSummaryInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  sessionTypeSummarySwatch: {
    width: 12,
    height: 12,
    borderRadius: 3,
    background: 'var(--color-border)',
    flexShrink: 0,
  },
  sessionTypeSummaryName: {
    fontFamily: 'var(--font-body)',
    fontSize: 13,
    color: 'var(--color-text)',
  },
  sessionTypeSummaryValue: {
    fontFamily: 'var(--font-data)',
    fontSize: 18,
    fontWeight: 500,
    color: 'var(--color-accent)',
  },
  typeGroupPanel: {
    marginBottom: 20,
    border: '1px solid var(--color-border)',
    borderRadius: 10,
    overflow: 'hidden',
    background: 'var(--color-surface)',
  },
  typeGroupHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 20px',
    cursor: 'pointer',
    userSelect: 'none',
    background: 'var(--color-surface)',
    borderBottom: '1px solid var(--color-border)',
    transition: 'background 0.15s',
  },
  typeGroupHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  typeGroupBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '4px 12px',
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 700,
    fontFamily: 'var(--font-heading)',
    letterSpacing: 1.2,
    textTransform: 'uppercase',
  },
  typeGroupCount: {
    fontFamily: 'var(--font-body)',
    fontSize: 13,
    color: 'var(--color-text-dim)',
  },
  typeGroupChevron: {
    fontFamily: 'var(--font-body)',
    fontSize: 16,
    color: 'var(--color-text-dim)',
    transition: 'transform 0.2s',
    lineHeight: 1,
  },
  typeGroupBody: {
    padding: '16px 20px',
  },
  searchInput: {
    width: '100%',
    maxWidth: 400,
    padding: '10px 14px',
    background: 'var(--color-bg)',
    border: '1px solid var(--color-border)',
    borderRadius: 6,
    color: 'var(--color-text)',
    fontFamily: 'var(--font-body)',
    fontSize: 14,
    outline: 'none',
    marginBottom: 16,
  },
};

const SESSION_TYPE_PRIORITY = ['race', 'qualifying', 'practice', 'demo'];
const TRACK_TYPE_ORDER = ['circuit', 'oval', 'karting', 'roval', 'street', 'autocross', 'hillclimb', 'dragstrip', 'dirt', 'unknown'];
const TRACK_TYPE_BADGE_STYLES = {
  circuit: { background: 'rgba(46, 134, 193, 0.16)', color: '#5dade2' },
  oval: { background: 'rgba(241, 196, 15, 0.16)', color: '#f4d03f' },
  karting: { background: 'rgba(39, 174, 96, 0.16)', color: '#58d68d' },
  roval: { background: 'rgba(155, 89, 182, 0.16)', color: '#c39bd3' },
  street: { background: 'rgba(230, 126, 34, 0.16)', color: '#f0b27a' },
  autocross: { background: 'rgba(231, 76, 60, 0.16)', color: '#f1948a' },
  hillclimb: { background: 'rgba(26, 188, 156, 0.16)', color: '#76d7c4' },
  dragstrip: { background: 'rgba(236, 112, 99, 0.16)', color: '#f5b7b1' },
  dirt: { background: 'rgba(160, 64, 0, 0.16)', color: '#d98880' },
  unknown: { background: 'rgba(127, 140, 141, 0.16)', color: '#bdc3c7' },
};

function getSessionTypePriority(sessionType) {
  const idx = SESSION_TYPE_PRIORITY.indexOf(String(sessionType || '').toLowerCase());
  return idx >= 0 ? idx : SESSION_TYPE_PRIORITY.length;
}

function sortSessionsNewestFirst(sessionList) {
  if (!Array.isArray(sessionList)) return [];
  return [...sessionList].sort((a, b) => {
    const dateCompare = (b.date || '').localeCompare(a.date || '');
    if (dateCompare !== 0) return dateCompare;
    return (b.createdAt || '').localeCompare(a.createdAt || '');
  });
}

function groupSessionsByTrack(sessionList) {
  const grouped = new Map();

  sortSessionsNewestFirst(sessionList).forEach((session) => {
    const trackName = session.track || 'Unknown Track';
    if (!grouped.has(trackName)) {
      grouped.set(trackName, []);
    }
    grouped.get(trackName).push(session);
  });

  return Array.from(grouped.entries())
    .map(([trackName, groupedSessions]) => ({
      trackName,
      sessions: groupedSessions,
      newestDate: groupedSessions[0]?.date || '',
    }))
    .sort((a, b) => (b.newestDate || '').localeCompare(a.newestDate || ''));
}

function groupSessionsByType(sessionList) {
  const groups = new Map();

  sessionList.forEach((session) => {
    const sessionType = (session.sessionType || 'Unknown').toLowerCase();
    if (!groups.has(sessionType)) {
      groups.set(sessionType, []);
    }
    groups.get(sessionType).push(session);
  });

  return Array.from(groups.entries())
    .map(([sessionType, typeSessions]) => ({
      sessionType,
      trackGroups: groupSessionsByTrack(typeSessions),
      count: typeSessions.length,
    }))
    .sort((a, b) => getSessionTypePriority(a.sessionType) - getSessionTypePriority(b.sessionType));
}

function getTrackTypeRawValue(track) {
  const rawValue = String(
    track?.trackType ||
    track?.tracktype ||
    track?.metadata?.trackType ||
    track?.metadata?.tracktype ||
    'unknown'
  ).trim();

  return rawValue || 'unknown';
}

function getTrackTypeLabel(trackType) {
  const formatted = String(trackType || 'unknown')
    .trim()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');

  return formatted.replace(/\b\w/g, (char) => char.toUpperCase());
}

function getTrackTypeBadgeStyle(trackType) {
  return TRACK_TYPE_BADGE_STYLES[trackType] || TRACK_TYPE_BADGE_STYLES.unknown;
}

export default function SessionsPage({ user, onSelectSession, onLogout, onViewTrack, onAdmin, onDashboard, onProfile, onImport, onBlog, initialTrackFilter }) {
  const [sessions, setSessions] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadStep, setLoadStep] = useState(0);
  const [activeTab, setActiveTab] = useState('sessions');
  const [trackSearch, setTrackSearch] = useState('');
  const [trackCountryFilter, setTrackCountryFilter] = useState('all');
  const [trackStateFilter, setTrackStateFilter] = useState('all');
  const [selectedTrack, setSelectedTrack] = useState(initialTrackFilter || 'all');
  const [selectedDate, setSelectedDate] = useState('all');
  const [selectedSessionType, setSelectedSessionType] = useState('Race');
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [sharedUsers, setSharedUsers] = useState([]);
  const [sharedLoading, setSharedLoading] = useState(false);
  const [pendingRequestCount, setPendingRequestCount] = useState(0);

  useEffect(() => {
    loadData();
  }, [user.uid]);

  const loadData = async () => {
    setLoading(true);
    setLoadStep(0);
    // Load sessions from local API
    try {
      const res = await fetch(`/api/sessions/${user.uid}`, { headers: authHeaders() });
      const sessionList = res.ok ? await res.json() : [];
      setSessions(Array.isArray(sessionList) ? sessionList : []);
    } catch (err) {
      console.error('Error loading sessions:', err);
      setSessions([]);
    }
    setLoadStep(1);

    // Load tracks from local API
    try {
      const res = await fetch('/api/tracks', { headers: authHeaders() });
      const trackList = await res.json();
      setTracks(Array.isArray(trackList) ? trackList : []);
    } catch (err) {
      console.error('Error loading tracks:', err);
    }

    setLoading(false);
  };

  const loadSharedData = useCallback(async (trackId) => {
    setSharedLoading(true);
    try {
      const users = await getSharedSessions(trackId || null);
      setSharedUsers(users);
    } catch (err) {
      console.error('Error loading shared sessions:', err);
    }
    setSharedLoading(false);
  }, []);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteTrackTarget, setDeleteTrackTarget] = useState(null);
  const [deletingTrack, setDeletingTrack] = useState(false);
  const [deleteTrackProgress, setDeleteTrackProgress] = useState('');
  const [collapsedTracks, setCollapsedTracks] = useState({});

  const handleDeleteSession = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const token = localStorage.getItem('rv_token');
      const res = await fetch(`/api/session/${user.uid}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ fileName: deleteTarget.fileName }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Delete failed');
      }
      setSessions((prev) => prev.filter((s) => s.fileName !== deleteTarget.fileName));
      setDeleteTarget(null);
    } catch (err) {
      console.error('Delete session error:', err);
      alert('Failed to delete session: ' + err.message);
    } finally {
      setDeleting(false);
    }
  };

  const loadPendingCount = useCallback(async () => {
    try {
      const { incoming } = await getRequests();
      setPendingRequestCount(incoming.length);
    } catch (err) {
      console.error('Error loading pending requests:', err);
    }
  }, []);

  useEffect(() => {
    loadPendingCount();
  }, [loadPendingCount]);

  useEffect(() => {
    if (activeTab === 'shared') {
      loadSharedData(selectedTrack !== 'all' ? selectedTrack?.replace(/\s+/g, '_') : null);
    }
  }, [activeTab, selectedTrack, loadSharedData]);

  const countryOptions = useMemo(() => {
    const countries = new Set();
    tracks.forEach((t) => {
      const c = t.metadata?.country;
      if (c) countries.add(c);
    });
    return [...countries].sort();
  }, [tracks]);

  const stateOptions = useMemo(() => {
    const states = new Set();
    tracks.forEach((t) => {
      if (trackCountryFilter !== 'all' && t.metadata?.country !== trackCountryFilter) return;
      const s = t.metadata?.state;
      if (s) states.add(s);
    });
    return [...states].sort();
  }, [tracks, trackCountryFilter]);

  useEffect(() => {
    if (trackStateFilter !== 'all' && !stateOptions.includes(trackStateFilter)) {
      setTrackStateFilter('all');
    }
  }, [trackStateFilter, stateOptions]);

  const filteredTracks = useMemo(() => tracks.filter((t) => {
    if (trackCountryFilter !== 'all' && t.metadata?.country !== trackCountryFilter) return false;
    if (trackStateFilter !== 'all' && t.metadata?.state !== trackStateFilter) return false;
    if (trackSearch && !(t.trackName || '').toLowerCase().includes(trackSearch.toLowerCase())) return false;
    return true;
  }), [tracks, trackSearch, trackCountryFilter, trackStateFilter]);

  const groupedTracks = useMemo(() => {
    const groups = new Map();

    filteredTracks.forEach((track) => {
      const rawTrackType = getTrackTypeRawValue(track);
      const trackType = normalizeTrackTypeValue(rawTrackType);
      if (!groups.has(trackType)) {
        groups.set(trackType, { rawTrackType, tracks: [] });
      }
      groups.get(trackType).tracks.push(track);
    });

    return Array.from(groups.entries())
      .map(([trackType, group]) => ({
        trackType,
        label: getTrackTypeLabel(group.rawTrackType),
        tracks: [...group.tracks].sort((a, b) => (a.trackName || '').localeCompare(b.trackName || '')),
        count: group.tracks.length,
      }))
      .sort((a, b) => {
        const aIndex = TRACK_TYPE_ORDER.indexOf(a.trackType);
        const bIndex = TRACK_TYPE_ORDER.indexOf(b.trackType);
        const normalizedA = aIndex === -1 ? TRACK_TYPE_ORDER.length : aIndex;
        const normalizedB = bIndex === -1 ? TRACK_TYPE_ORDER.length : bIndex;
        if (normalizedA !== normalizedB) return normalizedA - normalizedB;
        return a.label.localeCompare(b.label);
      });
  }, [filteredTracks]);

  const trackThumbMap = useMemo(() => {
    const map = {};
    for (const t of tracks) {
      if (t.hasThumbnail) map[t.trackId] = true;
    }
    return map;
  }, [tracks]);

  const sortedSessions = sortSessionsNewestFirst(sessions);
  const trackOptions = Array.from(
    new Set(sortedSessions.map((session) => session.track).filter(Boolean))
  );
  const dateSourceSessions = selectedTrack === 'all'
    ? sortedSessions
    : sortedSessions.filter((session) => session.track === selectedTrack);
  const dateOptions = Array.from(
    new Set(dateSourceSessions.map((session) => session.date).filter(Boolean))
  ).sort((a, b) => b.localeCompare(a));
  const sessionTypeOptions = Array.from(
    new Set(sortedSessions.map((session) => (session.sessionType || '').toLowerCase()).filter(Boolean))
  ).sort((a, b) => a.localeCompare(b));

  const filteredSessions = sortedSessions.filter((session) => {
    const matchesTrack = selectedTrack === 'all' || session.track === selectedTrack;
    const matchesDate = selectedDate === 'all' || session.date === selectedDate;
    const matchesSessionType = selectedSessionType === 'all' || (session.sessionType || '').toLowerCase() === selectedSessionType;
    return matchesTrack && matchesDate && matchesSessionType;
  });

  const totalsSourceSessions = sortedSessions.filter((session) => {
    const matchesTrack = selectedTrack === 'all' || session.track === selectedTrack;
    const matchesDate = selectedDate === 'all' || session.date === selectedDate;
    return matchesTrack && matchesDate;
  });

  const sessionTypeGroups = groupSessionsByType(filteredSessions);

  const todaySessions = useMemo(() => {
    const now = new Date();
    const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    return filteredSessions.filter(s => s.date === todayStr);
  }, [filteredSessions]);

  useEffect(() => {
    if (!loading && selectedTrack !== 'all' && !trackOptions.includes(selectedTrack)) {
      setSelectedTrack('all');
    }
  }, [selectedTrack, trackOptions, loading]);

  useEffect(() => {
    if (selectedDate !== 'all' && !dateOptions.includes(selectedDate)) {
      setSelectedDate('all');
    }
  }, [selectedDate, dateOptions]);

  useEffect(() => {
    if (selectedSessionType !== 'all' && !sessionTypeOptions.includes(selectedSessionType)) {
      setSelectedSessionType('all');
    }
  }, [selectedSessionType, sessionTypeOptions]);

  const summaryStats = useMemo(() => {
    const totalSessions = filteredSessions.length;
    const trackCount = new Set(filteredSessions.map((session) => session.track).filter(Boolean)).size;
    const bestLapSession = filteredSessions.reduce((best, session) => {
      if (!session?.bestLap) return best;
      if (!best || session.bestLap < best.bestLap) return session;
      return best;
    }, null);
    const fastestSession = filteredSessions.reduce((best, session) => {
      if ((session?.maxSpeed || 0) <= 0) return best;
      if (!best || (session.maxSpeed || 0) > (best.maxSpeed || 0)) return session;
      return best;
    }, null);
    const sessionTypeCounts = Object.entries(
      totalsSourceSessions.reduce((counts, session) => {
        const sessionType = (session.sessionType || 'Unknown').toLowerCase();
        counts[sessionType] = (counts[sessionType] || 0) + 1;
        return counts;
      }, {})
    ).sort((a, b) => {
      if (b[1] !== a[1]) return b[1] - a[1];
      return a[0].localeCompare(b[0]);
    });

    const buildDetails = (session, recordField) => {
      if (!session) return null;
      const lines = [];
      if (session.track) lines.push(session.track);
      if (session.car) lines.push(session.car);
      if (session.date) lines.push(getSessionDateLabel(session, tracks));
      return lines;
    };

    return {
      totalSessions,
      trackCount,
      bestLap: bestLapSession?.bestLap ?? null,
      bestLapDetails: buildDetails(bestLapSession),
      topSpeed: fastestSession?.maxSpeed ?? 0,
      topSpeedDetails: buildDetails(fastestSession),
      sessionTypeCounts,
    };
  }, [filteredSessions, totalsSourceSessions, tracks]);

  const resetFilters = () => {
    setSelectedTrack('all');
    setSelectedDate('all');
    setSelectedSessionType('all');
  };

  const toggleGroup = (sessionType) => {
    setCollapsedGroups((prev) => ({
      ...prev,
      [sessionType]: !prev[sessionType],
    }));
  };

  const toggleTrack = (key) => {
    setCollapsedTracks((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleDeleteTrackSessions = async () => {
    if (!deleteTrackTarget) return;
    setDeletingTrack(true);
    const token = localStorage.getItem('rv_token');
    const sessionsToDelete = deleteTrackTarget.sessions;
    const deleted = [];
    const failed = [];
    for (let i = 0; i < sessionsToDelete.length; i++) {
      const session = sessionsToDelete[i];
      setDeleteTrackProgress(`Deleting ${i + 1} of ${sessionsToDelete.length}...`);
      try {
        const res = await fetch(`/api/session/${user.uid}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ fileName: session.fileName }),
        });
        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.error || 'Delete failed');
        }
        deleted.push(session.fileName);
      } catch (err) {
        failed.push({ session, error: err.message });
      }
    }
    if (deleted.length > 0) {
      setSessions((prev) => prev.filter((s) => !deleted.includes(s.fileName)));
    }
    if (failed.length > 0) {
      alert(`Deleted ${deleted.length} session(s), but ${failed.length} failed: ${failed.map(f => f.error).join(', ')}`);
    }
    setDeleteTrackTarget(null);
    setDeletingTrack(false);
    setDeleteTrackProgress('');
  };

  const renderSessionCards = (sessionList, allowDelete = false) => (
    <div style={styles.grid}>
      {sessionList.map((session) => (
        <SessionTile
          key={session.sessionId || session.id}
          session={session}
          tracks={tracks}
          trackThumbMap={trackThumbMap}
          onClick={() => onSelectSession(session)}
          onDelete={allowDelete ? setDeleteTarget : undefined}
        />
      ))}
    </div>
  );

  const renderTrackGroups = (groups, sessionType) => (
    <>
      {groups.map((group) => {
        const collapseKey = `${sessionType}:${group.trackName}`;
        const isTrackCollapsed = collapsedTracks[collapseKey];
        return (
          <div key={group.trackName} style={styles.groupedTrackSection}>
            <div
              style={styles.groupedTrackHeader}
              onClick={() => toggleTrack(collapseKey)}
            >
              <div style={{
                ...styles.groupedTrackChevron,
                transform: isTrackCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
              }}>
                ▾
              </div>
              <div style={styles.groupedTrackLabel}>{group.trackName}</div>
              <div style={styles.groupedTrackMeta}>
                {group.sessions.length} session{group.sessions.length === 1 ? '' : 's'}
              </div>
              <div style={styles.groupedTrackLine} />
              <button
                style={styles.groupedTrackDeleteBtn}
                title={`Delete all ${group.sessions.length} session(s) for ${group.trackName}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setDeleteTrackTarget({
                    trackName: group.trackName,
                    sessionType: formatSessionTypeLabel(sessionType),
                    sessions: group.sessions,
                  });
                }}
                onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
                onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.4'; }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#e74c3c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                  <path d="M10 11v6" />
                  <path d="M14 11v6" />
                  <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                </svg>
              </button>
            </div>
            {!isTrackCollapsed && renderSessionCards(group.sessions, true)}
          </div>
        );
      })}
    </>
  );

  return (
    <div style={styles.page}>
      <Header user={user} onLogout={onLogout} onAdmin={onAdmin} onDashboard={onDashboard} onProfile={onProfile} />
      <div style={styles.content} data-doc-id="sessions-dashboard">
        <div style={styles.title}>DASHBOARD</div>

        {/* Tab navigation */}
        <div style={styles.sectionRow} data-doc-id="sessions-tabs">
          <button
            data-doc-id="sessions-tab-sessions"
            style={{ ...styles.tab, ...(activeTab === 'sessions' ? styles.tabActive : {}) }}
            onClick={() => setActiveTab('sessions')}
          >
            My Sessions
          </button>
          {user?.permissions?.allowLive && (
            <button
              data-doc-id="sessions-tab-live"
              style={{ ...styles.tab, ...(activeTab === 'live' ? styles.tabActive : {}) }}
              onClick={() => setActiveTab('live')}
            >
              Live Sessions
            </button>
          )}
          <button
            data-doc-id="sessions-tab-tracks"
            style={{ ...styles.tab, ...(activeTab === 'tracks' ? styles.tabActive : {}) }}
            onClick={() => setActiveTab('tracks')}
          >
            Track Database
          </button>
          <button
            data-doc-id="sessions-tab-shared"
            style={{ ...styles.tab, ...(activeTab === 'shared' ? styles.tabActive : {}), position: 'relative' }}
            onClick={() => setActiveTab('shared')}
          >
            Team Sessions
            {pendingRequestCount > 0 && (
              <span style={{
                position: 'absolute',
                top: -4,
                right: -4,
                background: '#e74c3c',
                color: '#fff',
                fontSize: 9,
                fontWeight: 700,
                width: 18,
                height: 18,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                {pendingRequestCount}
              </span>
            )}
          </button>
          {onBlog && (
            <button
              data-doc-id="sessions-blog-btn"
              style={{ ...styles.tab, marginLeft: 'auto', borderColor: 'var(--color-accent)', color: 'var(--color-accent)' }}
              onClick={onBlog}
            >
              Blog
            </button>
          )}
          {onImport && (
            <button
              data-doc-id="sessions-import-btn"
              style={{ ...styles.tab, marginLeft: onBlog ? 0 : 'auto', borderColor: 'var(--color-accent)', color: 'var(--color-accent)' }}
              onClick={onImport}
            >
              Import Data
            </button>
          )}
        </div>

        {loading ? (
          <LoadingProgress
            label="Loading your data"
            done={loadStep}
            total={2}
            detail={loadStep === 0 ? 'Fetching your sessions' : 'Fetching tracks'}
          />
        ) : (
          <>
            {/* Sessions Tab */}
            {activeTab === 'sessions' && (
              <div data-doc-id="sessions-panel">
                {sessions.length === 0 ? (
                  <div style={styles.emptyState}>
                    <div style={styles.emptyTitle}>No Sessions Yet</div>
                    <div style={styles.emptyText}>
                      Upload your first session from the RaceVoice mobile app<br />
                      or import telemetry data to see your track data here.
                    </div>
                  </div>
                ) : (
                  <>
                    <div style={styles.filterRow}>
                      <div style={styles.filterGroup}>
                        <div style={styles.filterLabel}>Filter By Track</div>
                        <select
                          data-doc-id="session-filter-track"
                          style={styles.filterSelect}
                          value={selectedTrack}
                          onChange={(e) => setSelectedTrack(e.target.value)}
                        >
                          <option value="all">All Tracks</option>
                          {trackOptions.map((trackName) => (
                            <option key={trackName} value={trackName}>
                              {trackName}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div style={styles.filterGroup}>
                        <div style={styles.filterLabel}>Session Style</div>
                        <select
                          data-doc-id="session-filter-style"
                          style={styles.filterSelect}
                          value={selectedSessionType}
                          onChange={(e) => setSelectedSessionType(e.target.value)}
                        >
                          <option value="all">All Styles</option>
                          {sessionTypeOptions.map((sessionType) => (
                            <option key={sessionType} value={sessionType}>
                              {sessionType}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div style={styles.filterGroup}>
                        <div style={styles.filterLabel}>Filter By Day</div>
                        <select
                          data-doc-id="session-filter-day"
                          style={styles.filterSelect}
                          value={selectedDate}
                          onChange={(e) => setSelectedDate(e.target.value)}
                        >
                          <option value="all">All Days</option>
                          {dateOptions.map((dateValue) => (
                            <option key={dateValue} value={dateValue}>
                              {formatDate(dateValue)}
                            </option>
                          ))}
                        </select>
                      </div>
                      <button style={styles.resetBtn} onClick={resetFilters} data-doc-id="session-filter-reset">
                        Clear Filters
                      </button>
                    </div>
                    <div style={styles.sectionDivider} data-doc-id="sessions-leaderboard">
                      <div style={styles.sectionDividerLabel}>Leaderboard</div>
                      <div style={styles.sectionDividerLine} />
                    </div>
                    <div style={styles.statsRow}>
                      <StatCard label="Sessions" value={summaryStats.totalSessions} color="var(--color-accent)" />
                      <StatCard label="Tracks" value={summaryStats.trackCount} />
                      <StatCard
                        label="Best Lap"
                        value={summaryStats.bestLap != null ? formatLapTime(summaryStats.bestLap) : '--:--.---'}
                        color="var(--color-accent)"
                        details={summaryStats.bestLapDetails}
                      />
                      <StatCard
                        label="Top Speed"
                        value={summaryStats.topSpeed > 0 ? formatSpeed(summaryStats.topSpeed) : '--'}
                        color="#27ae60"
                        details={summaryStats.topSpeedDetails}
                      />
                      <div style={styles.sessionTypeSummaryCard}>
                        <div style={styles.sessionTypeSummaryLabel}>Session Type Totals</div>
                        <div style={styles.sessionTypeSummaryList}>
                          {summaryStats.sessionTypeCounts.map(([sessionType, count]) => (
                            <div key={sessionType} style={styles.sessionTypeSummaryRow}>
                              <div style={styles.sessionTypeSummaryInfo}>
                                <SessionTypeIcon
                                  sessionType={sessionType}
                                  size={14}
                                  style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--color-text)' }}
                                />
                              </div>
                              <div style={styles.sessionTypeSummaryValue}>{count}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                    {todaySessions.length > 0 && (
                      <>
                        <div style={styles.sectionDivider}>
                          <div style={styles.sectionDividerLabel}>Today's Sessions</div>
                          <div style={styles.sectionDividerLine} />
                        </div>
                        {renderSessionCards(todaySessions, true)}
                      </>
                    )}
                    {filteredSessions.length === 0 ? (
                      <div style={styles.emptyState}>
                        <div style={styles.emptyTitle}>No Sessions Match These Filters</div>
                        <div style={styles.emptyText}>
                          Try a different track or day to broaden the list.
                        </div>
                      </div>
                    ) : null}
                    {sessionTypeGroups.map((group) => {
                      const isCollapsed = collapsedGroups[group.sessionType];
                      return (
                        <div key={group.sessionType} style={styles.typeGroupPanel} data-doc-id="session-type-group">
                          <div
                            style={{
                              ...styles.typeGroupHeader,
                              ...(isCollapsed ? { borderBottom: 'none' } : {}),
                            }}
                            data-doc-id="session-type-group-toggle"
                            onClick={() => toggleGroup(group.sessionType)}
                            onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.03)')}
                            onMouseLeave={(e) => (e.currentTarget.style.background = 'var(--color-surface)')}
                          >
                            <div style={styles.typeGroupHeaderLeft}>
                              <SessionTypeIcon
                                sessionType={group.sessionType}
                                size={18}
                                style={{
                                  fontFamily: 'var(--font-heading)',
                                  fontSize: 12,
                                  fontWeight: 700,
                                  letterSpacing: 1,
                                  textTransform: 'uppercase',
                                  color: 'var(--color-text)',
                                }}
                              />
                              <div style={styles.typeGroupCount}>
                                {group.count} session{group.count === 1 ? '' : 's'}
                              </div>
                            </div>
                            <div style={{
                              ...styles.typeGroupChevron,
                              transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                            }}>
                              ▾
                            </div>
                          </div>
                          {!isCollapsed && (
                            <div style={styles.typeGroupBody}>
                              {renderTrackGroups(group.trackGroups, group.sessionType)}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
            )}

            {/* Team Sessions Tab */}
            {activeTab === 'shared' && (
              <div data-doc-id="shared-panel">
                <ConnectionsPanel onConnectionsChange={() => {
                  loadPendingCount();
                  loadSharedData(selectedTrack !== 'all' ? selectedTrack?.replace(/\s+/g, '_') : null);
                }} />

                <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center' }}>
                  <div style={styles.filterGroup}>
                    <div style={styles.filterLabel}>Filter Team Sessions By Track</div>
                    <select
                      data-doc-id="shared-filter-track"
                      style={styles.filterSelect}
                      value={selectedTrack}
                      onChange={(e) => setSelectedTrack(e.target.value)}
                    >
                      <option value="all">All Tracks</option>
                      {trackOptions.map((trackName) => (
                        <option key={trackName} value={trackName}>
                          {trackName}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {sharedLoading ? (
                  <LoadingProgress label="Loading team sessions" />
                ) : sharedUsers.length === 0 ? (
                  <div style={styles.emptyState}>
                    <div style={styles.emptyTitle}>No Team Sessions</div>
                    <div style={styles.emptyText}>
                      Use the driver list above to send connection requests
                      <br />and start comparing data with your team.
                    </div>
                  </div>
                ) : (
                  sharedUsers.map((sharedUser) => (
                    <div key={sharedUser.userId} style={{ marginBottom: 24 }} data-doc-id="shared-user-group">
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        marginBottom: 12,
                        paddingBottom: 8,
                        borderBottom: '1px solid var(--color-border)',
                      }}>
                        <ProfileImage uid={sharedUser.userId} profileImage={sharedUser.profileImage} size={32} />
                        <div>
                          <div style={{
                            fontFamily: 'var(--font-heading)',
                            fontSize: 14,
                            fontWeight: 700,
                            color: 'var(--color-text)',
                          }}>
                            {sharedUser.displayName || sharedUser.email}
                          </div>
                          <div style={{
                            fontFamily: 'var(--font-body)',
                            fontSize: 11,
                            color: 'var(--color-text-dim)',
                          }}>
                            {sharedUser.sessions.length} session{sharedUser.sessions.length === 1 ? '' : 's'}
                          </div>
                        </div>
                      </div>
                      <div style={styles.grid}>
                        {sharedUser.sessions.map((session) => (
                          <SessionTile
                            key={session.sessionId || session.fileName}
                            session={session}
                            tracks={tracks}
                            trackThumbMap={trackThumbMap}
                            badge={{
                              label: sharedUser.displayName || sharedUser.email,
                              style: { color: '#2e86c1', background: 'rgba(46, 134, 193, 0.15)' },
                            }}
                            onClick={() => onSelectSession({
                              ...session,
                              isShared: true,
                              userId: sharedUser.userId,
                              ownerName: sharedUser.displayName || sharedUser.email,
                            })}
                          />
                        ))}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Live Sessions Tab */}
            {activeTab === 'live' && (
              <LiveTelemetryPanel />
            )}

            {/* Tracks Tab */}
            {activeTab === 'tracks' && (
              <div data-doc-id="tracks-panel">
                <div style={{
                  background: 'var(--color-surface)',
                  border: '1px solid var(--color-border)',
                  borderRadius: 10,
                  padding: '20px',
                  textAlign: 'center',
                  marginBottom: 16,
                }}>
                  <div style={{
                    fontFamily: 'var(--font-data)',
                    fontSize: 32,
                    fontWeight: 700,
                    color: 'var(--color-accent)',
                  }}>{tracks.length}</div>
                  <div style={{
                    fontFamily: 'var(--font-heading)',
                    fontSize: 10,
                    color: 'var(--color-text-dim)',
                    letterSpacing: 1.5,
                    textTransform: 'uppercase',
                    marginTop: 6,
                  }}>Total Tracks</div>
                  <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginTop: 10, flexWrap: 'wrap' }}>
                    {Object.entries(tracks.reduce((acc, t) => {
                      const type = t.metadata?.trackType || 'unknown';
                      acc[type] = (acc[type] || 0) + 1;
                      return acc;
                    }, {})).sort((a, b) => b[1] - a[1]).map(([type, count]) => (
                      <div key={type} style={{ textAlign: 'center' }}>
                        <div style={{ fontFamily: 'var(--font-data)', fontSize: 16, fontWeight: 600, color: 'var(--color-text)' }}>{count}</div>
                        <div style={{ fontFamily: 'var(--font-heading)', fontSize: 9, color: 'var(--color-text-dim)', letterSpacing: 1, textTransform: 'uppercase' }}>{type}</div>
                      </div>
                    ))}
                  </div>
                </div>
                <input
                  data-doc-id="tracks-search"
                  style={styles.searchInput}
                  type="text"
                  placeholder="Search tracks..."
                  value={trackSearch}
                  onChange={(e) => setTrackSearch(e.target.value)}
                />
                <div style={styles.filterRow}>
                  <div style={styles.filterGroup}>
                    <div style={styles.filterLabel}>Country</div>
                    <select
                      style={styles.filterSelect}
                      value={trackCountryFilter}
                      onChange={(e) => {
                        setTrackCountryFilter(e.target.value);
                        setTrackStateFilter('all');
                      }}
                    >
                      <option value="all">All Countries</option>
                      {countryOptions.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                  <div style={styles.filterGroup}>
                    <div style={styles.filterLabel}>State / Region</div>
                    <select
                      style={styles.filterSelect}
                      value={trackStateFilter}
                      onChange={(e) => setTrackStateFilter(e.target.value)}
                    >
                      <option value="all">All States / Regions</option>
                      {stateOptions.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                </div>
                {filteredTracks.length === 0 ? (
                  <div style={styles.emptyState}>
                    <div style={styles.emptyTitle}>No Tracks Available</div>
                    <div style={styles.emptyText}>
                      Tracks will appear here after an administrator syncs<br />
                      the track database from the master repository.
                    </div>
                  </div>
                ) : (
                  groupedTracks.map((group) => (
                    <div key={group.trackType} style={styles.typeGroupPanel} data-doc-id="track-type-group">
                      <div style={{ ...styles.typeGroupHeader, cursor: 'default' }}>
                        <div style={styles.typeGroupHeaderLeft}>
                          <span style={{ ...styles.typeGroupBadge, ...getTrackTypeBadgeStyle(group.trackType) }}>
                            <TrackTypeIcon trackType={group.trackType} size={16} />
                            {group.label}
                          </span>
                          <div style={styles.typeGroupCount}>
                            {group.count} track{group.count === 1 ? '' : 's'}
                          </div>
                        </div>
                      </div>
                      <div style={styles.typeGroupBody}>
                        <div style={styles.grid}>
                          {group.tracks.map((track) => (
                            <div
                              key={track.trackId}
                              style={styles.card}
                              data-doc-id="track-card"
                              onClick={() => onViewTrack && onViewTrack(track)}
                              onMouseEnter={(e) => (e.currentTarget.style.borderColor = 'var(--color-accent)')}
                              onMouseLeave={(e) => (e.currentTarget.style.borderColor = 'var(--color-border)')}
                            >
                              {track.hasThumbnail ? (
                                <TrackThumbnail
                                  trackId={track.trackId}
                                  alt=""
                                  style={{ width: '100%', height: 100, objectFit: 'contain', borderRadius: 6, marginBottom: 12 }}
                                />
                              ) : (
                                <div style={{ width: '100%', height: 100, borderRadius: 6, background: 'var(--color-bg)', marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  <span style={{ fontFamily: 'var(--font-heading)', fontSize: 10, color: 'var(--color-text-dim)', letterSpacing: 1, textTransform: 'uppercase' }}>No Preview</span>
                                </div>
                              )}
                              <div style={styles.trackName}>
                                {track.trackName}
                              </div>
                              {track.isCustom && <span style={styles.customBadge}>Custom</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Delete confirmation modal */}
      {deleteTarget && (
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center',
            justifyContent: 'center', zIndex: 9999,
          }}
          onClick={() => !deleting && setDeleteTarget(null)}
        >
          <div
            style={{
              background: 'var(--color-surface)', border: '1px solid var(--color-border)',
              borderRadius: 12, padding: 24, maxWidth: 400, width: '90%',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              fontFamily: 'var(--font-heading)', fontSize: 16, fontWeight: 700,
              color: 'var(--color-text)', marginBottom: 12,
            }}>
              Delete Session?
            </div>
            <div style={{
              fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--color-text-dim)',
              marginBottom: 12,
            }}>
              {deleteTarget.trackName || deleteTarget.track} — {formatDate(deleteTarget.date)}
            </div>
            <div style={{
              background: 'var(--color-bg)', borderRadius: 8, padding: '12px 16px',
              marginBottom: 12, fontFamily: 'var(--font-body)', fontSize: 13,
              color: 'var(--color-text-dim)', lineHeight: 1.8,
            }}>
              {deleteTarget.car && <div><strong style={{ color: 'var(--color-text)' }}>Car:</strong> {deleteTarget.car}</div>}
              {deleteTarget.lapCount != null && <div><strong style={{ color: 'var(--color-text)' }}>Laps:</strong> {deleteTarget.lapCount}</div>}
              {deleteTarget.bestLap != null && <div><strong style={{ color: 'var(--color-text)' }}>Best Lap:</strong> {formatLapTime(deleteTarget.bestLap)}</div>}
              {deleteTarget.sessionType && <div><strong style={{ color: 'var(--color-text)' }}>Type:</strong> {formatSessionTypeLabel(deleteTarget.sessionType)}</div>}
              {deleteTarget.deviceType && <div><strong style={{ color: 'var(--color-text)' }}>Source:</strong> {deleteTarget.deviceType}</div>}
            </div>
            <div style={{
              fontFamily: 'var(--font-body)', fontSize: 13, color: '#e74c3c',
              marginBottom: 20, fontWeight: 600,
            }}>
              This will permanently delete the session data, videos, and all associated files. This cannot be undone.
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <button
                onClick={handleDeleteSession}
                disabled={deleting}
                style={{
                  flex: 1, padding: '10px 20px', background: '#e74c3c', border: 'none',
                  borderRadius: 6, color: '#fff', fontFamily: 'var(--font-heading)',
                  fontSize: 14, fontWeight: 600, cursor: deleting ? 'not-allowed' : 'pointer',
                  opacity: deleting ? 0.6 : 1, letterSpacing: 1,
                }}
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
              <button
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
                style={{
                  flex: 1, padding: '10px 20px', background: 'transparent',
                  border: '1px solid var(--color-border)', borderRadius: 6,
                  color: 'var(--color-text-dim)', fontFamily: 'var(--font-heading)',
                  fontSize: 14, fontWeight: 600, cursor: 'pointer', letterSpacing: 1,
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Bulk track delete confirmation modal */}
      {deleteTrackTarget && (
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center',
            justifyContent: 'center', zIndex: 9999,
          }}
          onClick={() => !deletingTrack && setDeleteTrackTarget(null)}
        >
          <div
            style={{
              background: 'var(--color-surface)', border: '1px solid var(--color-border)',
              borderRadius: 12, padding: 24, maxWidth: 500, width: '90%', maxHeight: '80vh',
              overflowY: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              fontFamily: 'var(--font-heading)', fontSize: 16, fontWeight: 700,
              color: 'var(--color-text)', marginBottom: 4,
            }}>
              Delete All Sessions for Track?
            </div>
            <div style={{
              fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--color-text-dim)',
              marginBottom: 16,
            }}>
              {deleteTrackTarget.trackName} — {deleteTrackTarget.sessionType}
            </div>
            <div style={{
              fontFamily: 'var(--font-heading)', fontSize: 11, color: 'var(--color-text-dim)',
              letterSpacing: 1.2, textTransform: 'uppercase', marginBottom: 8,
            }}>
              {deleteTrackTarget.sessions.length} session{deleteTrackTarget.sessions.length === 1 ? '' : 's'} will be deleted:
            </div>
            <div style={{
              background: 'var(--color-bg)', borderRadius: 8, padding: '8px 0',
              marginBottom: 16, maxHeight: 240, overflowY: 'auto',
            }}>
              {deleteTrackTarget.sessions.map((session, idx) => (
                <div key={session.fileName || idx} style={{
                  padding: '8px 16px',
                  borderBottom: idx < deleteTrackTarget.sessions.length - 1 ? '1px solid var(--color-border)' : 'none',
                  fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--color-text-dim)',
                  lineHeight: 1.6,
                }}>
                  <div style={{ color: 'var(--color-text)', fontWeight: 600, marginBottom: 2 }}>
                    {formatDate(session.date)}{session.car ? ` — ${session.car}` : ''}
                  </div>
                  <div style={{ fontSize: 12 }}>
                    {session.lapCount != null && <span>{session.lapCount} laps</span>}
                    {session.bestLap != null && <span> · Best: {formatLapTime(session.bestLap)}</span>}
                    {session.maxSpeed != null && <span> · {formatSpeed(session.maxSpeed)}</span>}
                    {session.deviceType && <span> · {session.deviceType}</span>}
                  </div>
                </div>
              ))}
            </div>
            <div style={{
              fontFamily: 'var(--font-body)', fontSize: 13, color: '#e74c3c',
              marginBottom: 20, fontWeight: 600,
            }}>
              This will permanently delete all session data, videos, and associated files for this track. This cannot be undone.
            </div>
            {deleteTrackProgress && (
              <div style={{
                fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--color-accent)',
                marginBottom: 12,
              }}>
                {deleteTrackProgress}
              </div>
            )}
            <div style={{ display: 'flex', gap: 12 }}>
              <button
                onClick={handleDeleteTrackSessions}
                disabled={deletingTrack}
                style={{
                  flex: 1, padding: '10px 20px', background: '#e74c3c', border: 'none',
                  borderRadius: 6, color: '#fff', fontFamily: 'var(--font-heading)',
                  fontSize: 14, fontWeight: 600, cursor: deletingTrack ? 'not-allowed' : 'pointer',
                  opacity: deletingTrack ? 0.6 : 1, letterSpacing: 1,
                }}
              >
                {deletingTrack ? 'Deleting...' : `Delete ${deleteTrackTarget.sessions.length} Session${deleteTrackTarget.sessions.length === 1 ? '' : 's'}`}
              </button>
              <button
                onClick={() => setDeleteTrackTarget(null)}
                disabled={deletingTrack}
                style={{
                  flex: 1, padding: '10px 20px', background: 'transparent',
                  border: '1px solid var(--color-border)', borderRadius: 6,
                  color: 'var(--color-text-dim)', fontFamily: 'var(--font-heading)',
                  fontSize: 14, fontWeight: 600, cursor: 'pointer', letterSpacing: 1,
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
